/**
 * HTTP session service — gateway `/sessions` endpoints.
 */

import { apiClient, toServiceError } from '@/services/http/client'
import { toCamelDeep } from '@/services/http/caseMap'
import type { SessionService, SessionStats } from './sessions.service'
import { parseUserAgent, type UserSession } from '@/types/session.types'

function mapSession(raw: Record<string, unknown>): UserSession {
  const s = toCamelDeep<Record<string, unknown>>(raw)
  // Backend returns is_active; the UI keys off `status`.
  if (s.status == null) {
    s.status = s.isActive === true ? 'active' : s.isActive === false ? 'inactive' : 'unknown'
  }
  const metadata = parseUserAgent(typeof s.userAgent === 'string' ? s.userAgent : '')
  for (const [key, value] of Object.entries(metadata)) {
    if (!s[key]) s[key] = value
  }
  return s as unknown as UserSession
}

function mapSessionList(raw: unknown): UserSession[] {
  const arr = Array.isArray(raw) ? raw : ((raw as { items?: unknown[] })?.items ?? [])
  return (arr as Record<string, unknown>[]).map(mapSession)
}

const EMPTY_SESSION_STATS: SessionStats = { totalActive: 0, byDevice: {}, byLocation: [] }

export function createHttpSessionService(): SessionService {
  return {
    async listForUser(userId: string): Promise<UserSession[]> {
      try {
        const res = await apiClient.get('/sessions', { params: { user_id: userId } })
        return mapSessionList(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async listAllActive(): Promise<UserSession[]> {
      try {
        const res = await apiClient.get('/sessions', { params: { status: 'active' } })
        return mapSessionList(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async getStats(): Promise<SessionStats> {
      // Stats are a non-critical aggregate; degrade gracefully if the endpoint
      // is absent rather than breaking the page.
      try {
        const res = await apiClient.get('/sessions/stats')
        return toCamelDeep<SessionStats>(res.data)
      } catch {
        return EMPTY_SESSION_STATS
      }
    },

    async terminate(sessionId: string): Promise<boolean> {
      try {
        await apiClient.delete(`/sessions/${sessionId}`)
        return true
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async terminateAllOther(_userId: string, _currentSessionId: string): Promise<number> {
      // Backend: DELETE /sessions revokes all of the current user's sessions
      // except the one in the JWT (returns 204, no count).
      try {
        await apiClient.delete('/sessions')
        return 0
      } catch (err) {
        throw toServiceError(err)
      }
    },
  }
}
